import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmpContext } from './SmpContext';

import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import SocialPostsGrid from '@app/components/social-posts/SocialPostsGrid';
import SocialPostsList from '@app/components/social-posts/SocialPostsList';

import { Button as ButtonAntd, Spin, Flex, Divider, Row, Col } from '@app/ui';
import { PlusOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';

import { getCarouselPostIds } from './carouselGroups';

const SocialMediaViews = () => {
    const { t } = useTranslation('measurement');
    const {
        isSelectMode,
        view,
        showExistingPosts,
        selectablePosts,
        selectablePostsOrder,
        currency,
        selectedPosts,
        setSelectedPosts,
        displayDetails,
        collections,
        totals,
        tableColumns,
        infiniteScrollEnabled,
        postsOrder,
        scrollBottom,
        isLoading,
        hasPostsAvailable,
        morePages,
        nextPage,
        scrollTop,
    } = useSmpContext();

    const onSelectPost = useCallback(
        postId => {
            const post = selectablePosts[postId];
            const postIds = getCarouselPostIds(post);
            setSelectedPosts(currentSelected => {
                const nextSelected = { ...currentSelected };
                const shouldSelect = !postIds.some(id => !!currentSelected[id]);
                postIds.forEach(id => {
                    nextSelected[id] = shouldSelect;
                });
                return nextSelected;
            });
        },
        [selectablePosts, setSelectedPosts],
    );

    return (
        <Row gutter={16} className="-m-b-6">
            {view === 'grid' && showExistingPosts && (
                <Col span={24}>
                    <ErrorBoundary FallbackComponent={CrashedComponent}>
                        <SocialPostsGrid
                            posts={selectablePosts}
                            postsOrder={selectablePostsOrder}
                            currency={currency}
                            isSelectMode={isSelectMode}
                            selected={selectedPosts}
                            onDetailedView={displayDetails}
                            onSelectPost={onSelectPost}
                            collections={collections.list.items}
                        />
                    </ErrorBoundary>
                </Col>
            )}
            {view === 'list' && showExistingPosts && (
                <Col md={24}>
                    <ErrorBoundary FallbackComponent={CrashedComponent}>
                        <SocialPostsList
                            posts={selectablePosts}
                            postsOrder={selectablePostsOrder}
                            currency={currency}
                            totals={totals}
                            isSelectMode={isSelectMode}
                            selected={selectedPosts}
                            setSelected={setSelectedPosts}
                            onDetailedView={displayDetails}
                            onSelectPost={onSelectPost}
                            postLevelMetrics={tableColumns.postLevelMetrics}
                            organicAndPaidMetrics={
                                tableColumns.organicAndPaidMetrics
                            }
                            showCollabPostInfo={
                                tableColumns.showCollabPostInfo ?? false
                            }
                        />
                    </ErrorBoundary>
                </Col>
            )}

            {infiniteScrollEnabled && postsOrder.length > 10 && (
                <>
                    <Col md={24}>
                        <button
                            id="arrow-down"
                            type="button"
                            className="gds-circular-button gds-circular-button--sm gds-circular-button--gray"
                            onClick={scrollBottom}
                        >
                            <i className="fas fa-arrow-to-bottom fa-lg" />
                        </button>
                    </Col>
                </>
            )}
            <Col md={24}>
                {isLoading && (
                    <Flex
                        justify="center"
                        align="center"
                        style={{
                            height: 400,
                        }}
                    >
                        <Spin size="large" className="-p-t-5" />
                    </Flex>
                )}

                {!hasPostsAvailable && !isLoading && (
                    <h2 className="gds-text--header-xs -color-tx-lt-5 -text-center -m-t-5">
                        {t('socialMediaExposure.views.noSocialPosts')}
                    </h2>
                )}

                {infiniteScrollEnabled && !isLoading && (
                    <div className="-m-a-4 gds-flex gds-flex--justify-around">
                        {morePages && hasPostsAvailable && (
                            <ButtonAntd
                                type="primary"
                                icon={<PlusOutlined />}
                                size="small"
                                onClick={nextPage}
                                style={{
                                    padding: '0px 15px',
                                    fontFamily: 'pnregular',
                                }}
                            >
                                {t('socialMediaExposure.views.loadMore')}
                            </ButtonAntd>
                        )}

                        {!morePages && hasPostsAvailable && (
                            <>
                                <Divider
                                    style={{
                                        fontFamily: 'pnregular',
                                    }}
                                >
                                    {t(
                                        'socialMediaExposure.views.endOfResults',
                                    )}
                                </Divider>
                                <ButtonAntd
                                    type="primary"
                                    shape="circle"
                                    icon={<VerticalAlignTopOutlined />}
                                    onClick={scrollTop}
                                />
                            </>
                        )}
                    </div>
                )}
            </Col>
            <Col md={24}>
                <div id="bottom" />
            </Col>
        </Row>
    );
};
export default SocialMediaViews;
